// =============================================================================
// Thin API client for the SPA → Pages Functions
// =============================================================================
// Sends the Clerk session JWT as `Authorization: Bearer <token>` when called
// inside a component that has the Clerk session available. Otherwise relies
// on the __session cookie that Clerk sets automatically.
// =============================================================================

import { useAuth } from "@clerk/clerk-react"
import { NetworkError, fetchWithRetry } from "./fetchRetry"
import { useCallback } from "react"

export { NetworkError }

export class ApiError extends Error {
  status: number
  details: unknown
  constructor(status: number, message: string, details?: unknown) {
    super(message)
    this.status = status
    this.details = details
  }
}

async function request<T>(
  path: string,
  init: RequestInit & { token?: string | null } = {},
): Promise<T> {
  const { token, headers, ...rest } = init
  const finalHeaders = new Headers(headers)
  if (token) finalHeaders.set("authorization", `Bearer ${token}`)
  if (rest.body && !finalHeaders.has("content-type")) {
    finalHeaders.set("content-type", "application/json")
  }
  const res = await fetchWithRetry(path, { ...rest, headers: finalHeaders })
  const text = await res.text()
  let parsed: unknown = null
  try {
    parsed = text ? JSON.parse(text) : null
  } catch {
    // Keep parsed = null
  }
  if (!res.ok) {
    const message =
      (parsed && typeof parsed === "object" && "message" in parsed && typeof (parsed as Record<string, unknown>).message === "string"
        ? (parsed as { message: string }).message
        : `HTTP ${res.status}`) ?? `HTTP ${res.status}`
    throw new ApiError(res.status, message, parsed)
  }
  return parsed as T
}

/**
 * Hook returning a function that issues authenticated API calls.
 * The Clerk session JWT is fetched via `getToken()` on each call so it
 * always has a fresh, unexpired token.
 */
export function useApi() {
  const { getToken } = useAuth()
  return useCallback(
    async <T,>(path: string, init: RequestInit = {}): Promise<T> => {
      const token = await getToken().catch(() => null)
      return request<T>(path, { ...init, token })
    },
    [getToken],
  )
}

// Unauthenticated request — used for /api/counter, /api/cities, /api/click.
export const api = {
  get: <T,>(path: string) => request<T>(path, { method: "GET" }),
  post: <T,>(path: string, body: unknown) =>
    request<T>(path, { method: "POST", body: JSON.stringify(body) }),
}
